// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './PipelineView.scss';
import Button from "../../../../nor/ui/components/button/Button";
import Icon from "../../../../nor/ui/components/icon/Icon";
import {
    EditIcon, MembersIcon,
    TrashIcon,
    UndoIcon
} from "../../../../assets/icon";
import {UNDO_HISTORY_LENGTH} from "../../../../constants/common";
import {ChangeCallback, VoidCallback} from "../../../../nor/ts/interfaces/callbacks";
import FormAppClassName from "../../../../constants/FormAppClassName";
import PipelineService from "../../../../services/PipelineService";
import RepositoryEntry from "../../../../nor/ts/simpleRepository/types/RepositoryEntry";
import LogService from "../../../../nor/ts/LogService";
import Loader from "../../../../nor/ui/components/loader/Loader";
import AppRoute, {
    getAppRoute_PIPELINE_RUN_LIST_VIEW
} from "../../../../constants/AppRoute";
import RouteService from "../../../../nor/ui/services/RouteService";
import PipelineModel from "../../../../nor/pipeline/types/PipelineModel";
import PipelineDTO from "../../../../nor/pipeline/dto/PipelineDTO";
import Pipeline from "../../../common/pipeline/Pipeline";
import { Link } from "react-router-dom";
import { ObserverDestructor } from "../../../../nor/ts/Observer";
import FormSaveModal from "../../../../modals/FormSaveModal";
import MemberListModal from "../../../../modals/MemberListModal";
import RepositoryMember from "../../../../nor/ts/simpleRepository/types/RepositoryMember";

const LOG = LogService.createLogger('PipelineView');

export const PIPELINE_SAVE_TIMEOUT = 200;

export enum PipelineViewComponentState {

    UNLOADED,
    LOADING,
    LOADED,
    SAVING,
    LOAD_ERROR,
    SAVE_ERROR,
    DESTROYED

}

export interface PipelineViewState {

    readonly id               : string | undefined;
    readonly version          : number;
    readonly model            : PipelineModel | undefined;
    readonly memberList       : RepositoryMember[];
    readonly modelHistory     : PipelineModel[];
    readonly componentState   : PipelineViewComponentState;

    readonly prevVersion ?: number | undefined;
    readonly prevModel   ?: PipelineModel | undefined;

}

export interface PipelineViewProps {

    readonly id         : string;
    readonly hasSession : boolean;

}

export class PipelineView extends React.Component<PipelineViewProps, PipelineViewState> {

    private readonly _changeModelCallback      : ChangeCallback<PipelineModel>;
    private readonly _undoModelCallback        : VoidCallback;
    private readonly _deleteCallback           : VoidCallback;
    private readonly _saveModalSubmitCallback  : VoidCallback;
    private readonly _saveModalCancelCallback  : VoidCallback;
    private readonly _openMembersCallback          : VoidCallback;

    private _componentState           : PipelineViewComponentState;
    private _openSaveModalTimeout     : any | undefined;
    private _saveModalSubmitListener  : ObserverDestructor | undefined;
    private _saveModalCancelListener  : ObserverDestructor | undefined;

    public constructor (props: PipelineViewProps) {

        super(props);

        this._componentState = PipelineViewComponentState.UNLOADED;

        this._openSaveModalTimeout = undefined;

        this.state = {
            id: undefined,
            model: undefined,
            version: 0,
            memberList: [],
            modelHistory: [],
            componentState: this._componentState
        };

        this._changeModelCallback          = this._onChangeModel.bind(this);
        this._undoModelCallback            = this._onUndoModel.bind(this);
        this._deleteCallback               = this._onDelete.bind(this);
        this._saveModalSubmitCallback      = this._onSaveModalSubmit.bind(this);
        this._saveModalCancelCallback      = this._onSaveModalCancel.bind(this);
        this._openMembersCallback          = this._onOpenMembers.bind(this);

    }

    public componentDidMount (): void {

        this._loadBackendItem();

    }

    public componentWillUnmount (): void {

        if (this._openSaveModalTimeout !== undefined) {
            clearTimeout(this._openSaveModalTimeout);
            this._openSaveModalTimeout = undefined;
            // FIXME: Propably should ask user if we want to save
            // LOG.debug(`componentWillUnmount: Saving now.`);
            // this._saveNow();
        }

        if (this._saveModalSubmitListener) {
            this._saveModalSubmitListener();
            this._saveModalSubmitListener = undefined;
        }

        if (this._saveModalCancelListener) {
            this._saveModalCancelListener();
            this._saveModalCancelListener = undefined;
        }

        if (FormSaveModal.isOpen()) {
            FormSaveModal.closeModal();
        }

        this._componentState = PipelineViewComponentState.DESTROYED;

    }

    public componentDidUpdate (prevProps: Readonly<PipelineViewProps>, prevState: Readonly<PipelineViewState>, snapshot?: any): void {

        if (this.props.hasSession && !prevProps.hasSession && this.state.componentState !== PipelineViewComponentState.LOADING) {
            this._loadBackendItem();
        }

    }

    public render () {

        const model          = this.state?.model;
        const modelVersion   = this.state.version;
        const modelHistory   = this.state.modelHistory;
        const componentState = this.state.componentState;
        const isLoading      = componentState === PipelineViewComponentState.LOADING;
        const isSaving       = componentState === PipelineViewComponentState.SAVING;

        // FIXME: Implement error state handling

        return (
            <div className={FormAppClassName.PIPELINE_VIEW}>

                <header className={FormAppClassName.PIPELINE_VIEW + '-header'}>

                    <h3 className={FormAppClassName.PIPELINE_VIEW + '-header-title'}>Pipeline {model?.name} v{modelVersion}</h3>

                    <div className={FormAppClassName.PIPELINE_VIEW + '-header-main-buttons'}>

                        {this.props.hasSession ? (
                            <React.Fragment>

                                <Link to={getAppRoute_PIPELINE_RUN_LIST_VIEW(this.props.id)}
                                      className={FormAppClassName.PIPELINE_VIEW + '-button'}>
                                    <Icon><EditIcon /></Icon>
                                    <span className={FormAppClassName.PIPELINE_VIEW + '-button-text'}>View Runs</span>
                                </Link>

                                <Button click={this._openMembersCallback}><Icon><MembersIcon /></Icon></Button>

                            </React.Fragment>
                        ) : null}

                        {modelHistory.length ? (
                            <Button click={this._undoModelCallback}><Icon><UndoIcon /></Icon></Button>
                        ): null}

                        {isSaving ? (
                            <Loader className={FormAppClassName.PIPELINE_VIEW + '-editor-loader'} />
                        ) : null}

                    </div>

                    <div className={FormAppClassName.PIPELINE_VIEW + '-header-side-buttons'}>

                        {this.props.hasSession ? (
                            <Button
                                className={FormAppClassName.PIPELINE_VIEW + '-delete-button'}
                                click={this._deleteCallback}><Icon><TrashIcon /></Icon> Delete pipeline</Button>
                        ) : null}

                    </div>

                </header>

                <div className={FormAppClassName.PIPELINE_VIEW + '-editor'}>

                    {this.props.hasSession ? (
                        <Pipeline
                            className={FormAppClassName.PIPELINE_VIEW + '-editor-field'}
                            model={model}
                            change={this._changeModelCallback}
                        />
                    ) : (
                        <div>
                            <strong>You need to log in.</strong>
                        </div>
                    )}

                </div>

                {isLoading ? (
                    <Loader className={FormAppClassName.PIPELINE_VIEW + '-loader'} />
                ) : null}

            </div>
        );

    }


    private _setModel (model: PipelineModel) {

        const origModel        = this.state?.model;
        const origModelHistory = this.state.modelHistory;

        let modelHistory : PipelineModel[] = origModel ? [
            origModel,
            ...origModelHistory
        ] : origModelHistory;

        if (modelHistory.length >= UNDO_HISTORY_LENGTH) {
            modelHistory = modelHistory.slice(0, UNDO_HISTORY_LENGTH);
        }

        this.setState({
            model: model,
            modelHistory: modelHistory
        });

        this._openSaveModalLater();

    }

    private _onChangeModel (model: PipelineModel) {
        this._setModel(model);
    }

    private _onUndoModel () {

        const [
            prevModel,
            ...modelHistory
        ] = this.state.modelHistory;

        this.setState({
            model: prevModel,
            modelHistory: modelHistory
        });

        this._openSaveModalLater();

    }

    private _loadBackendItem () {

        if ( this._componentState === PipelineViewComponentState.LOADING
            || this._componentState === PipelineViewComponentState.SAVING
        ) {
            LOG.warn(`_loadBackendItem: Already loading or saving`);
            return;
        }

        LOG.debug(`_loadBackendItem: Loading now.`);

        this._componentState = PipelineViewComponentState.LOADING;

        this.setState({componentState: this._componentState});

        PipelineService.getPipeline(this.props.id).then((item : RepositoryEntry<PipelineDTO>) => {

            this._componentState = PipelineViewComponentState.LOADED;

            this.setState({
                componentState : this._componentState,
                id             : item.id,
                version        : item.version,
                model          : item.data.model,
                memberList     : item?.members ?? []
            });

        }).catch(err => {

            this._componentState = PipelineViewComponentState.LOAD_ERROR;

            LOG.error(`_loadBackendItem: Loading failed: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

        });

    }

    private _saveNow () {

        if ( this._componentState === PipelineViewComponentState.LOADING
            || this._componentState === PipelineViewComponentState.SAVING
        ) {
            LOG.warn(`_saveForm: Already loading or saving form`);
            return;
        }

        LOG.debug(`_saveForm: Saving form now.`);

        this._componentState = PipelineViewComponentState.SAVING;

        this.setState({componentState: this._componentState});

        const model = this.state?.model;

        if (!model) {
            LOG.error(`_saveBackendItem: Model not defined.`);
            return;
        }

        PipelineService.savePipeline(
            this.props.id,
            model
        ).then((form : RepositoryEntry<PipelineDTO>) => {

            this._componentState = PipelineViewComponentState.LOADED;

            if (this._openSaveModalTimeout !== undefined) {

                LOG.info(`New save is pending.`);

            } else {

                LOG.info(`Save finished.`);

                this.setState({
                    componentState : this._componentState,
                    id             : form.id,
                    version        : form.version,
                    model          : form.data.model,
                    prevVersion    : form.version,
                    prevModel      : form.data.model
                });

            }

        }).catch(err => {

            this._componentState = PipelineViewComponentState.SAVE_ERROR;

            LOG.error(`_saveBackendItem: Failed to save: `, err);

            // FIXME: Display error

            this.setState({
                componentState: this._componentState
            });

            FormSaveModal.openModal(this._componentState === PipelineViewComponentState.SAVE_ERROR);

        });

    }

    private _openSaveModalLater () {

        if (this._openSaveModalTimeout !== undefined) {
            clearTimeout(this._openSaveModalTimeout);
            this._openSaveModalTimeout = undefined;
        }

        LOG.debug(`_openSaveModalLater: Triggered save later.`);

        this._openSaveModalTimeout = setTimeout( () => {

            this._openSaveModalTimeout = undefined;

            if (this._componentState === PipelineViewComponentState.DESTROYED ) {
                LOG.debug(`_openSaveModalLater: Already destroyed`);
                return;
            }

            if ( this._componentState === PipelineViewComponentState.LOADING
                || this._componentState === PipelineViewComponentState.SAVING ) {
                LOG.debug(`_openSaveModalLater: Already/still loading or saving form`);
                this._openSaveModalLater();
                return;
            }

            LOG.debug(`_openSaveModalLater: Saving form now.`);
            this._openSaveModal();

        }, PIPELINE_SAVE_TIMEOUT)

    }

    private _onDelete () {

        // FIXME: Implement a spinner
        // FIXME: Implement a confirmation or undo modal

        const id = this.props.id;
        PipelineService.deletePipeline(id).then(() => {

            LOG.info(`Removed ${id}`);

            RouteService.setRoute(AppRoute.PIPELINE_LIST_VIEW);

        }).catch(err => {

            // FIXME: Display UI error
            LOG.error(`Could not delete ${id}: `, err);

        });

    }

    private _openSaveModal () {

        if ( this._componentState === PipelineViewComponentState.LOADING
            || this._componentState === PipelineViewComponentState.SAVING ) {
            LOG.debug(`_openSaveModal: Already loading or saving form`);
            this._openSaveModalLater();
            return;
        }

        if (this._saveModalSubmitListener) {
            this._saveModalSubmitListener();
            this._saveModalSubmitListener = undefined;
        }

        if (this._saveModalCancelListener) {
            this._saveModalCancelListener();
            this._saveModalCancelListener = undefined;
        }

        this._saveModalSubmitListener = FormSaveModal.on(FormSaveModal.Event.SAVE, this._saveModalSubmitCallback);
        this._saveModalCancelListener = FormSaveModal.on(FormSaveModal.Event.CANCEL, this._saveModalCancelCallback);

        FormSaveModal.openModal(this._componentState === PipelineViewComponentState.SAVE_ERROR);

    }

    private _onSaveModalSubmit () {

        this._saveNow();

    }

    private _onSaveModalCancel () {

        if (   this.state?.prevVersion !== undefined
            && this.state?.prevModel !== undefined
        ) {

            LOG.debug(`Canceling to previous state ${this.state.prevVersion}`);

            this._componentState = PipelineViewComponentState.LOADED;

            this.setState({
                componentState   : this._componentState,
                version          : this.state.prevVersion,
                model            : this.state.prevModel,
                modelHistory     : []
            });

        } else {
            LOG.debug(`Cannot cancel: No previous state saved`);
        }

    }

    private _onOpenMembers () {
        MemberListModal.openModal(this.state.memberList);
    }

}

export default PipelineView;
